import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { Loader2, ShieldCheck, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import useAccessibleOverlay from "@/hooks/useAccessibleOverlay";

/**
 * LegalAcceptanceModal: read-to-bottom legal acceptance gate.
 *
 * Props:
 *  - open: bool: controlled visibility
 *  - onClose: () => void  (only called when `cancelable`, defaults to false)
 *  - onAccept: ({ legal_bundle_version }) => void
 *  - mode: "customer" | "business" | "staff"
 *      • customer : terms + privacy + cookie + acceptable-use + customer-terms
 *      • business : everything above + business-terms
 *      • staff    : terms + privacy + acceptable-use
 *  - title, subtitle (optional override)
 *  - cancelable: bool
 *  - acceptCta: string (defaults to "I agree: create my account")
 *
 * Behaviour:
 *  - Loads /api/compliance/legal-bundle on mount to render the canonical
 *    summary bullets straight from the backend (so we never drift).
 *  - Tracks scroll position inside the legal-text container; the accept
 *    button stays disabled until the user has scrolled to within 32px of
 *    the bottom AND has ticked the explicit confirmation checkbox.
 */
export default function LegalAcceptanceModal({
  open,
  onClose,
  onAccept,
  mode = "customer",
  title,
  subtitle,
  cancelable = true,
  acceptCta,
}) {
  const [bundle, setBundle] = useState(null);
  const [bundleErr, setBundleErr] = useState("");
  const [scrolledEnd, setScrolledEnd] = useState(false);
  const [checked, setChecked] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const scrollRef = useRef(null);
  const { panelRef } = useAccessibleOverlay(open, cancelable ? onClose : undefined);

  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    api.get("/compliance/legal-bundle")
      .then((r) => {
        if (!alive) return;
        setBundleErr("");
        setBundle(r.data);
        setScrolledEnd(false);
        setChecked(false);
      })
      .catch(() => {
        if (!alive) return;
        setBundle(null);
        setScrolledEnd(false);
        setChecked(false);
        setBundleErr("Couldn't load the latest legal terms. Please refresh the page and try again.");
      });
    return () => { alive = false; };
  }, [open]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (remaining <= 32) setScrolledEnd(true);
  };

  // If the content fits without overflow, count it as already scrolled.
  useEffect(() => {
    if (!open || !bundle) return undefined;
    const el = scrollRef.current;
    if (!el) return undefined;
    // Defer to a microtask so we don't touch state synchronously inside
    // the effect body.
    const id = setTimeout(() => {
      if (el.scrollHeight <= el.clientHeight + 4) setScrolledEnd(true);
    }, 0);
    return () => clearTimeout(id);
  }, [open, bundle]);

  if (!open) return null;

  const businessOnly = mode === "business";
  const docs = (bundle?.documents || []).filter((d) => {
    if (mode === "business") return true;
    if (mode === "staff") {
      return ["terms", "privacy", "acceptable-use"].includes(d.slug);
    }
    // customer
    return d.slug !== "business-terms";
  });

  // Not every document is the same kind of act. Contracts are agreed to; the
  // Privacy Policy is a notice the person is given; optional cookies are
  // chosen separately through the cookie banner and are never a condition of
  // creating an account.
  const requiredDocsForMode = docs.filter((d) =>
    (d.treatment || "accept") === "accept" && (d.required || (businessOnly && d.business_required)),
  );
  const noticesForMode = docs.filter((d) => d.treatment === "acknowledge");
  const preferencesForMode = docs.filter((d) => d.treatment === "preference");

  const canAccept = scrolledEnd && checked && bundle && !accepting;

  const accept = async () => {
    if (!canAccept) return;
    try {
      setAccepting(true);
      await onAccept({ legal_bundle_version: bundle.legal_bundle_version });
    } finally {
      setAccepting(false);
    }
  };

  const heading = title || (
    mode === "business" ? "Accept the legal terms to create your business"
    : mode === "staff"  ? "Accept the legal terms to activate your staff account"
    :                     "Accept the legal terms to create your account"
  );

  const helper = subtitle || (
    mode === "business"
      ? "ConfirmPH is a software / listing / booking-confirmation platform, not the rental business. Please read the summary below and the linked documents."
      : mode === "staff"
      ? "These terms describe how you may use ConfirmPH on behalf of your business. Read them in full before continuing."
      : "Please read the summary below. You'll be asked to confirm before your account is created."
  );

  return (
    <div
      ref={panelRef}
      tabIndex={-1}
      className="fixed inset-0 z-[80] bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      data-testid="legal-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="legal-modal-title"
    >
      <div className="bg-white w-full sm:max-w-2xl sm:rounded-2xl rounded-t-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        <div className="px-5 sm:px-6 py-4 border-b border-slate-200 flex items-start gap-3 bg-emerald-50/60">
          <ShieldCheck className="w-6 h-6 text-emerald-700 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h2
              id="legal-modal-title"
              className="font-display text-lg sm:text-xl font-semibold text-slate-900"
              data-testid="legal-modal-title"
            >
              {heading}
            </h2>
            <p className="mt-1 text-xs sm:text-sm text-slate-600">{helper}</p>
            {bundle?.legal_bundle_version && (
              <p className="mt-1 text-[11px] font-mono text-slate-500" data-testid="legal-modal-version">
                Legal bundle version: {bundle.legal_bundle_version}
              </p>
            )}
          </div>
        </div>

        <div
          ref={scrollRef}
          onScroll={onScroll}
          className="flex-1 overflow-y-auto px-5 sm:px-6 py-4 text-sm leading-relaxed text-slate-700 space-y-4"
          data-testid="legal-modal-scroll"
        >
          {bundleErr && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {bundleErr}
            </div>
          )}
          {!bundle && !bundleErr && (
            <div className="flex items-center gap-2 text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading the latest terms…
            </div>
          )}

          {bundle && (
            <>
              <section>
                <h3 className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-500 mb-2">
                  Plain-English summary
                </h3>
                <ul className="space-y-2 list-disc pl-5">
                  {(bundle.summary || []).map((line, i) => (
                    <li key={i} data-testid={`legal-summary-${i}`}>{line}</li>
                  ))}
                </ul>
              </section>

              <section>
                <h3 className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-500 mb-2">
                  Documents you are agreeing to
                </h3>
                <ul className="space-y-1.5">
                  {requiredDocsForMode.map((d) => (
                    <li key={d.slug} className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span>
                        <Link
                          to={d.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-medium text-[#1B4332] underline"
                          data-testid={`legal-doc-${d.slug}`}
                        >
                          {d.label}
                        </Link>
                        {d.slug === "business-terms" && ": required for business accounts"}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>

              {noticesForMode.length > 0 && (
                <section data-testid="legal-notices">
                  <h3 className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-500 mb-2">
                    Given to you to read
                  </h3>
                  <ul className="space-y-1.5">
                    {noticesForMode.map((d) => (
                      <li key={d.slug}>
                        <Link to={d.url} target="_blank" rel="noopener noreferrer"
                              className="font-medium text-[#1B4332] underline"
                              data-testid={`legal-doc-${d.slug}`}>
                          {d.label}
                        </Link>
                        : explains how your personal data is processed. It is a notice, not a
                        contract you sign.
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {preferencesForMode.length > 0 && (
                <section data-testid="legal-preferences">
                  <h3 className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-500 mb-2">
                    Chosen separately
                  </h3>
                  <ul className="space-y-1.5">
                    {preferencesForMode.map((d) => (
                      <li key={d.slug}>
                        <Link to={d.url} target="_blank" rel="noopener noreferrer"
                              className="font-medium text-[#1B4332] underline"
                              data-testid={`legal-doc-${d.slug}`}>
                          {d.label}
                        </Link>
                        : optional cookies are chosen through the cookie banner. Creating an
                        account does not require them.
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {bundle.support_email && (
                <section className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p>
                    Contact:{" "}
                    <a className="underline" href={`mailto:${bundle.support_email}`}>{bundle.support_email}</a>.
                    You can request account and data deletion at any time from your Settings page or
                    by writing to that address.
                  </p>
                </section>
              )}
            </>
          )}
        </div>

        <div className="px-5 sm:px-6 py-4 border-t border-slate-200 bg-white space-y-3">
          {!scrolledEnd && bundle && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2"
               data-testid="legal-scroll-hint">
              Scroll to the bottom of the terms above to enable the accept button.
            </p>
          )}
          <label className="flex items-start gap-3 text-sm text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              disabled={!scrolledEnd}
              data-testid="legal-modal-checkbox"
              className="mt-0.5 w-4 h-4 rounded border-slate-400 text-emerald-700 focus:ring-emerald-600"
            />
            <span>
              I agree to the documents listed under “Documents you are agreeing to”
              {businessOnly && ", including the Business Terms"}, and I have been given the
              Privacy Policy.
            </span>
          </label>
          <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
            {cancelable && (
              <button
                type="button"
                onClick={onClose}
                data-testid="legal-modal-cancel"
                className="px-4 py-2.5 rounded-lg border border-slate-300 text-sm font-medium hover:bg-slate-50">
                Cancel
              </button>
            )}
            <button
              type="button"
              onClick={accept}
              disabled={!canAccept}
              data-testid="legal-modal-accept"
              className="px-4 py-2.5 rounded-lg btn-brand text-sm font-medium disabled:opacity-50 inline-flex items-center justify-center gap-2">
              {accepting && <Loader2 className="w-4 h-4 animate-spin" />}
              {acceptCta || (mode === "business"
                ? "I agree: create my business"
                : mode === "staff"
                ? "I agree: activate my staff account"
                : "I agree: create my account")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
